import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'components/context/AuthContext';
import { useGoogleLogin, useFacebookLogin } from 'hooks';
import { FcGoogle } from 'react-icons/fc';
import { FaFacebook } from 'react-icons/fa';
import { Mail, ArrowLeft } from 'lucide-react';
import authApi from '../../api/auth';
import toast from 'react-hot-toast';
import AuthLayout from 'components/auth/AuthLayout';

const Login = () => {
  const navigate = useNavigate();
  const { loginWithEmail } = useAuth();
  const { startGoogleOAuthLogin, loading: googleLoading, error: googleError } = useGoogleLogin();
  const { startFacebookLogin, loading: facebookLoading, error: facebookError } = useFacebookLogin();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);

  const isLoading = emailLoading || googleLoading || facebookLoading;
  const globalError = emailError || googleError || facebookError;

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      navigate('/home');
    }
  }, [navigate]);

  const handleEmailLoginSubmit = async (e) => {
    e.preventDefault();
    setEmailLoading(true);
    setEmailError(null);

    try {
      await loginWithEmail(email, password);
      navigate('/home');
    } catch (err) {
      const message = err.response?.data?.error || 'Falló el inicio de sesión. Por favor, verifica tus credenciales.';
      setEmailError(message);
    } finally {
      setEmailLoading(false);
      setTimeout(() => {
        setEmailError(null);
      }, 3000);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotPasswordLoading(true);
    setForgotPasswordSuccess(false);

    try {
      await authApi.forgotPassword(forgotPasswordEmail);
      setForgotPasswordSuccess(true);
      toast.success('Se ha enviado un enlace de restablecimiento a tu correo electrónico');
    } catch (err) {
      const message = err.response?.data?.error || 'Error al enviar el enlace de restablecimiento. Por favor, intenta nuevamente.';
      toast.error(message);
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  return (
    <AuthLayout>
          {/* if not reset password, show this */}
          {!showForgotPassword && (
          <div className="text-center">
            <img className="mx-auto h-20 w-auto" src="/ch3.png" alt="Logo de la empresa" />
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Inicia sesión en tu cuenta</h2>
          </div>
          )}
          {globalError && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">{globalError}</h3>
                </div>
              </div>
            </div>
          )}

          {showForgotPassword ? (
            <div className="mt-8 space-y-6">
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotPasswordEmail('');
                    setForgotPasswordSuccess(false);
                  }}
                  className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-500 mb-4"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Volver al inicio de sesión
                </button>
                <h3 className="text-lg font-medium text-gray-900">Restablecer contraseña</h3>
                <p className="text-sm text-gray-600 mt-2">
                  Ingresa tu correo electrónico y te enviaremos un enlace para restablecer tu contraseña.
                </p>
              </div>

              {forgotPasswordSuccess ? (
                <div className="rounded-md bg-green-50 p-4">
                  <div className="flex">
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-green-800">
                        ¡Enlace enviado!
                      </h3>
                      <p className="text-sm text-green-700 mt-1">
                        Revisa tu correo electrónico y sigue las instrucciones para restablecer tu contraseña.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-6">
                  <div>
                    <label htmlFor="forgot-email" className="sr-only">Correo electrónico</label>
                    <input
                      id="forgot-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                      placeholder="Correo electrónico"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      disabled={forgotPasswordLoading}
                    />
                  </div>

                  <div>
                    <button
                      type="submit"
                      className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                      disabled={forgotPasswordLoading || !forgotPasswordEmail.trim()}
                    >
                      {forgotPasswordLoading ? (
                        <>
                          <Mail className="w-4 h-4 mr-2 animate-pulse" />
                          Enviando...
                        </>
                      ) : (
                        <>
                          <Mail className="w-4 h-4 mr-2" />
                          Enviar enlace de restablecimiento
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          ) : (
            <form className="mt-8 space-y-6" onSubmit={handleEmailLoginSubmit}>
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="email" className="sr-only">Correo electrónico</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                  placeholder="Correo electrónico"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                />
              </div>
              <div>
                <label htmlFor="password" className="sr-only">Contraseña</label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                  placeholder="Contraseña"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                />
              </div>
            </div>
  
            <div>
              <button
                type="submit"
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                disabled={isLoading}
              >
                {emailLoading ? 'Iniciando sesión...' : 'Iniciar sesión'}
              </button>
            </div>

            <div className="text-center text-sm text-gray-600">
              ¿Olvidaste tu contraseña?&nbsp;
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-indigo-600 hover:text-indigo-500 font-medium"
              >
                Hacé click aquí
              </button>
            </div>

          </form>
          )}

          {!showForgotPassword && (
            <>
              <div className="mt-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">O continúa con</span>
                  </div>
                </div>

                <div className="mt-6 text-center">
                  <p className="text-sm text-gray-600">
                    ¿No tienes una cuenta?{' '}
                    <a href="/register" className="font-medium text-indigo-600 hover:text-indigo-500">
                      Regístrate aquí
                    </a>
                  </p>
                </div>

                <div className="mt-6 grid grid-cols-2 gap-3">
                  <button
                    onClick={startGoogleOAuthLogin}
                    className="w-full flex items-center justify-center px-8 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    disabled={googleLoading}
                  >
                    <FcGoogle className="h-5 w-5 mr-2" />
                    Google
                  </button>

                  <button
                    onClick={startFacebookLogin}
                    className="w-full flex items-center justify-center px-8 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    disabled={facebookLoading}
                  >
                    <FaFacebook className="h-5 w-5 mr-2 text-blue-600" />
                    Facebook
                  </button>
                </div>
              </div>
            </>
          )}
    </AuthLayout>
  );
};

export default Login;
